// Thin authenticated fetch + JWT template constant. Each resource
// module imports authedFetch and builds its own typed hook.

import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined;
if (!API_BASE) throw new Error('VITE_API_BASE_URL is not set');

// Clerk JWT template. Must mint a token whose `aud` matches CLERK_AUDIENCE
// (when configured) per cloud/functions/src/auth/clerk.ts. No role claim
// is required — authentication alone authorizes every endpoint.
export const CLERK_JWT_TEMPLATE = 'ufh-api';

export interface ApiError extends Error {
  status: number;
  detail?: unknown;
}

export function apiError(message: string, status: number, detail?: unknown): ApiError {
  const err = new Error(message) as ApiError;
  err.status = status;
  err.detail = detail;
  return err;
}

export function useAuthedFetch() {
  const { getToken } = useAuth();
  return useCallback(
    async (path: string, init?: RequestInit): Promise<Response> => {
      const token = await getToken({ template: CLERK_JWT_TEMPLATE });
      const headers = new Headers(init?.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return fetch(`${API_BASE}${path}`, { ...init, headers });
    },
    [getToken],
  );
}

export async function parseJsonOrThrow<T>(
  res: Response,
  label: string,
  parser: (raw: unknown) => T,
): Promise<T> {
  if (!res.ok) {
    let detail: unknown = undefined;
    try {
      detail = await res.json();
    } catch {
      /* body may not be JSON */
    }
    throw apiError(`${label} ${res.status}`, res.status, detail);
  }
  const raw = await res.json();
  return parser(raw);
}

// For body-less responses (typically DELETE). Reads the body for error
// detail when the status isn't OK; otherwise resolves with nothing.
export async function throwIfNotOk(res: Response, label: string): Promise<void> {
  if (res.ok) return;
  let detail: unknown = undefined;
  try {
    detail = await res.json();
  } catch {
    /* body may not be JSON */
  }
  throw apiError(`${label} ${res.status}`, res.status, detail);
}
